import { useState } from "react"
import { useOutletContext } from "react-router-dom"
import ToolShell from "../components/ToolShell"
import CTABox from "../components/CTABox"
import FormulaBox from "../components/FormulaBox"
import ToolFAQ from "../components/ToolFAQ"
import RelatedToolsList from "../components/RelatedToolsList"
import { ToolDef } from "../toolRegistry"
import { AlertCircle } from "lucide-react"

export default function MortgageAffordabilityCalculatorPage() {
  const { tool, currency } = useOutletContext<{ tool: ToolDef, currency: string }>()

  const [monthlyRent, setMonthlyRent] = useState(1200)
  const [interestRate, setInterestRate] = useState(5.5)
  const [icrOption, setIcrOption] = useState<"125" | "145" | "manual">("145")
  const [manualIcr, setManualIcr] = useState(145)
  const [loanTerm, setLoanTerm] = useState(25)
  const [deposit, setDeposit] = useState(50000)

  const activeIcr = icrOption === "manual" ? manualIcr : Number(icrOption)

  // BTL ICR calculations
  const annualRent = monthlyRent * 12
  const maxAnnualInterest = activeIcr > 0 ? annualRent / (activeIcr / 100) : 0
  const maxLoanAmount = interestRate > 0 ? maxAnnualInterest / (interestRate / 100) : 0
  const maxPropertyValue = maxLoanAmount + deposit
  const depositPct = maxPropertyValue > 0 ? (deposit / maxPropertyValue) * 100 : 0
  const monthlyInterestPayment = (maxLoanAmount * (interestRate / 100)) / 12

  const isDepositLow = depositPct < 25

  return (
    <ToolShell>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6 sm:p-8">
        
        {/* Input Column */}
        <div className="space-y-6">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Expected Monthly Rent</label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                <input type="number" value={monthlyRent || ''} onChange={(e) => setMonthlyRent(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary bg-white" />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="text-sm font-medium text-slate-700">ICR Requirement (Interest Coverage)</label>
                <div title="Lenders require rent to cover 125%-145% of the interest payment to buffer for voids/taxes." className="cursor-help text-xs text-primary font-medium bg-primary/10 px-2 py-0.5 rounded-full">?</div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <button onClick={() => setIcrOption("125")} className={`py-2 text-sm font-medium rounded-lg border transition-colors ${icrOption === "125" ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
                  125% (Basic)
                </button>
                <button onClick={() => setIcrOption("145")} className={`py-2 text-sm font-medium rounded-lg border transition-colors ${icrOption === "145" ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
                  145% (Higher)
                </button>
                <button onClick={() => setIcrOption("manual")} className={`py-2 text-sm font-medium rounded-lg border transition-colors ${icrOption === "manual" ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
                  Custom...
                </button>
              </div>
              {icrOption === "manual" && (
                <div className="mt-2 relative">
                  <span className="absolute right-3 top-2.5 text-slate-400">%</span>
                  <input type="number" value={manualIcr || ''} onChange={(e) => setManualIcr(Number(e.target.value))} className="w-full pl-4 pr-8 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary" placeholder="Enter custom ICR %" />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Mortgage Interest Rate: {interestRate}%</label>
              <input type="range" min="1" max="10" step="0.1" value={interestRate} onChange={(e) => setInterestRate(Number(e.target.value))} className="w-full accent-primary" />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Cash Deposit Available</label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                <input type="number" value={deposit || ''} onChange={(e) => setDeposit(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary bg-white" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Loan Term (Years)</label>
              <input type="number" value={loanTerm || ''} onChange={(e) => setLoanTerm(Number(e.target.value))} className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary bg-white" />
            </div>
          </div>
        </div>

        {/* Results Column */}
        <div className="space-y-6">
          <div className="bg-white border shadow-sm border-slate-200 p-6 sm:p-8 rounded-2xl flex flex-col items-center text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 p-3 opacity-10">
              <span className="text-9xl font-black">{currency}</span>
            </div>
            
            <span className="text-sm font-medium text-slate-500 mb-1 relative z-10">Max Property Value (Purchase Price)</span>
            <span className="text-5xl sm:text-6xl font-extrabold text-slate-900 tracking-tight relative z-10">{currency}{maxPropertyValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
            
            <div className="w-full mt-8 grid grid-cols-2 gap-4 relative z-10 border-t border-slate-100 pt-6">
              <div>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Max Loan</div>
                <div className="text-xl font-bold text-primary">{currency}{maxLoanAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Interest /mo</div>
                <div className="text-xl font-bold text-slate-700">{currency}{monthlyInterestPayment.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
              </div>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4">
            <div className={`flex items-start gap-3 p-3 rounded-lg border ${isDepositLow ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-green-50 border-green-200 text-green-800'}`}>
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-sm">Deposit creates a {depositPct.toFixed(1)}% LTV</div>
                <div className="text-sm mt-1 opacity-90">
                  {isDepositLow 
                    ? "Warning: Most Buy-to-Let mortgages require a 25% minimum deposit. You may need more cash to buy a property at this valuation."
                    : "Good: This meets standard 25% minimum deposit requirements for BTL products."}
                </div>
              </div>
            </div>
            
            <div className="text-sm text-slate-600 border-t border-slate-200 pt-3">
              <span className="font-medium text-slate-900">ICR Validation: </span> 
              Your expected rent ({currency+monthlyRent}) is exactly {activeIcr}% of the calculated interest payment ({currency+monthlyInterestPayment.toFixed(0)}), meeting the lender stress test.
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 sm:px-8 pb-10">
        <FormulaBox 
          title="Buy-to-Let Mortgage Formulas"
          formula={`Max Annual Interest Permitted = (Expected Rent × 12) / (ICR / 100)\n\nMax Loan Amount = Max Annual Interest / (Mortgage Rate / 100)\n\nMax Property Valuation = Max Loan + Cash Deposit`} 
        />
        <CTABox icon={tool.icon} heading="Track your growing property wealth" body={tool.ctaText} ctaLabel="Join for Free" ctaHref="/register" />
        <ToolFAQ 
          items={[
            { q: "What is ICR?", a: "Interest Coverage Ratio. Traditional residential mortgages check your personal income. BTL mortgages instead check if the rental income comfortably covers the mortgage interest. A 145% ICR means the rent must be 1.45x higher than the mortgage interest payment." },
            { q: "Why did you use interest-only logic?", a: "Most BTL investors utilize interest-only mortgages to maximize initial cash flow and rely on capital appreciation for overall returns. Lenders usually stress test BTL affordability purely on the interest repayment chunk." }
          ]} 
        />
        <RelatedToolsList relatedSlugs={tool.related} />
      </div>
    </ToolShell>
  )
}
